import { z } from "zod";

export const commentPositionSchema = z.object({
  viewportWidth: z.number().int().positive(),
  pageX: z.number().finite(),
  pageY: z.number().finite(),
  selector: z.string().min(1).max(2000).nullable(),
  offsetX: z.number().min(0).max(1),
  offsetY: z.number().min(0).max(1),
});
export type CommentPosition = z.infer<typeof commentPositionSchema>;

// Reject anything that isn't a normal http(s) URL — guards against data:,
// javascript:, file:, etc. being smuggled into the proxy fetch.
const httpUrl = z
  .string()
  .url()
  .max(2048)
  .refine(
    (v) => {
      try {
        const u = new URL(v);
        return u.protocol === "http:" || u.protocol === "https:";
      } catch {
        return false;
      }
    },
    { message: "URL must be http or https" },
  );

export const createProjectSchema = z.object({
  name: z.string().trim().min(1).max(120),
  targetUrl: httpUrl,
  // When true, the worker crawls the target site after creating the project
  // and bulk-creates one named page per discovered HTML page. Defaults to
  // false so existing clients keep their single-URL flow.
  discover: z.boolean().optional().default(false),
});

export type DiscoveryStatus = "ok" | "partial" | "failed";

export type ProjectSummary = {
  id: string;
  name: string;
  targetUrl: string;
  shareToken: string;
  archivedAt: number | null;
  createdAt: number;
  // Attribution for the shared Avista workspace. Null only if the
  // original creator row has been deleted out from under the project.
  createdByEmail: string | null;
  createdByName: string | null;
  // Activity counters surfaced for the project list view.
  openCommentCount: number;
  resolvedCommentCount: number;
};

export type ShareLookupResult = {
  project: {
    id: string;
    name: string;
    targetUrl: string;
  };
};

// Project pages — friendly named entries on top of the URL-scoped comment
// model. `path` is leading-slash, no scheme/host/fragment; the server
// validates this to keep pages within the project's target origin.
const pagePath = z
  .string()
  .trim()
  .min(1)
  .max(2048)
  .refine(
    (v) => {
      if (!v.startsWith("/")) return false;
      if (v.startsWith("//")) return false; // protocol-relative — would jump origin
      try {
        // Confirm it parses as a relative URL against an arbitrary base —
        // catches things like " /foo", "/foo bar", control chars, etc.
        new URL(v, "https://example.com/");
        return true;
      } catch {
        return false;
      }
    },
    { message: "Path must start with `/` and stay within the project's origin" },
  );

export const createPageSchema = z.object({
  name: z.string().trim().min(1).max(120),
  path: pagePath,
});

export const renamePageSchema = z.object({
  name: z.string().trim().min(1).max(120),
});

// Bulk reorder — body is the full project's page IDs in their new order.
// Server sets orderIndex = array position for each. Bounded to a sane max
// to keep the bind-parameter count well under D1's 100-param ceiling.
export const reorderPagesSchema = z.object({
  pageIds: z.array(z.string().uuid()).min(1).max(80),
});

export type ApiPage = {
  id: string;
  name: string;
  path: string;
  fullUrl: string;
  orderIndex: number;
  openCommentCount: number;
  totalCommentCount: number;
  // Owner-controlled per-page visibility for collaborators. Hidden pages
  // never appear in the share-token page list; owners see them with a
  // closed-eye affordance so they can unhide. Always false on the share
  // surface (it strips them upstream).
  hidden: boolean;
};

// Phase 8 — Client portal.

export const addInviteeSchema = z.object({
  name: z.string().trim().min(1).max(80),
  email: z.string().email().max(320),
});

export const clientLoginSchema = z.object({
  email: z.string().email().max(320),
  // Optional context — when a customer signs up directly from a share link
  // we use this to bind a project_invitees row at consume time, so the
  // newly minted client session has at least one project to land on.
  shareToken: z.string().trim().min(1).max(128).optional(),
});

export const clientConsumeSchema = z.object({
  token: z.string().trim().min(1).max(128),
  shareToken: z.string().trim().min(1).max(128).optional(),
});

export type ApiInvitee = {
  id: string;
  name: string;
  email: string;
  status: "active" | "revoked";
  createdAt: number;
  lastSeenAt: number | null;
};

export type ApiClientMe = {
  email: string;
  name: string | null;
};

export type ApiClientProject = {
  id: string;
  name: string;          // invitee-specific display name (project_invitees.name)
  projectName: string;   // canonical project name (projects.name)
  targetUrl: string;
  shareToken: string;    // reused so the existing share-token comment surface works
  openCommentCount: number;
};

export const createCommentSchema = z.object({
  pageUrl: z.string().max(2048),
  parentId: z.string().uuid().nullable().optional(),
  authorName: z.string().trim().min(1).max(80),
  authorEmail: z.string().email().max(320),
  body: z.string().trim().min(1).max(4000),
  position: commentPositionSchema,
});

// Owner edit — authorization is by session.user.email matching the comment's
// stored authorEmail. No need to repeat the email in the body.
export const editCommentSchema = z.object({
  body: z.string().trim().min(1).max(4000),
});

// Reviewer self-edit — authorEmail in the body is matched against the stored
// authorEmail. Localstorage-based identity is unverified but consistent with
// how reviewer commenting already works.
export const editCommentByShareSchema = z.object({
  body: z.string().trim().min(1).max(4000),
  authorEmail: z.string().email().max(320),
});

// Owner accounts are gated to Avista employees — Clarity is the platform
// Avista uses to collaborate with their customers, so non-Avista emails sign
// up via the client portal (magic link), not here.
export const OWNER_EMAIL_DOMAIN = "avista.is";

export const signupSchema = z.object({
  email: z
    .string()
    .email()
    .max(320)
    .refine((e) => e.toLowerCase().endsWith(`@${OWNER_EMAIL_DOMAIN}`), {
      message: `Owner accounts require an @${OWNER_EMAIL_DOMAIN} email`,
    }),
  password: z.string().min(8).max(128),
  name: z.string().trim().min(1).max(80),
});

export const loginSchema = z.object({
  email: z.string().email().max(320),
  password: z.string().min(1).max(128),
});

// Personal API tokens. `name` is a label for the owner ("Laptop plugin");
// the secret itself is only ever returned by the create call.
export const createApiTokenSchema = z.object({
  name: z.string().trim().min(1).max(80),
});

export type ApiTokenSummary = {
  id: string;
  name: string;
  createdAt: number;
  lastUsedAt: number | null;
  revokedAt: number | null;
};

// Phase 14 — Code Mode subtree capture for high-fidelity Breakdance copy.
// A CapturedNode is one element in the clicked subtree: tag identity,
// curated computed styles, geometry, and (for nodes whose mapping needs
// raw markup — lists, embeds) a capped outerHTML. The overlay enforces
// the caps (max nodes / depth / html size); the schema just validates
// shape.
export type CapturedNode = {
  tag: string;
  id: string | null;
  classes: string[];
  // Stable CSS selector for this node (same buildSelector the overlay uses
  // for comment pins). Optional for backward-compat with older bundles.
  // Phase 18: lets the translator apply owner element-markings per node.
  selector?: string;
  text: string | null;
  href: string | null;
  src: string | null;
  alt: string | null;
  rect: { width: number; height: number };
  styles: Record<string, string>;
  html: string | null;
  children: CapturedNode[];
};

export const capturedNodeSchema: z.ZodType<CapturedNode> = z.lazy(() =>
  z.object({
    tag: z.string(),
    id: z.string().nullable(),
    classes: z.array(z.string()),
    selector: z.string().optional(),
    text: z.string().nullable(),
    href: z.string().nullable(),
    src: z.string().nullable(),
    alt: z.string().nullable(),
    rect: z.object({ width: z.number(), height: z.number() }),
    styles: z.record(z.string()),
    html: z.string().nullable(),
    children: z.array(capturedNodeSchema),
  }),
);

// Breakdance clipboard element shape — the recursive tree Breakdance reads
// on paste. Used by the deterministic mapper (Phase 14) and validated when
// Claude generates a payload (Phase 15). `properties` is intentionally an
// opaque bag — Breakdance's design schema is deep and element-specific, so
// we validate structure (id / type / children / _parentId) but not the
// interior of `properties`.
export type BDElement = {
  id: number;
  data: {
    type: string;
    properties: Record<string, unknown> | null;
  };
  children: BDElement[];
  _parentId: number;
};

export const bdElementSchema: z.ZodType<BDElement> = z.lazy(() =>
  z.object({
    id: z.number(),
    data: z.object({
      type: z.string(),
      properties: z.record(z.unknown()).nullable(),
    }),
    children: z.array(bdElementSchema),
    _parentId: z.number(),
  }),
);

// Top-level clipboard envelope: { element: <BDElement> }.
export const bdClipboardSchema = z.object({ element: bdElementSchema });
export type BDClipboard = z.infer<typeof bdClipboardSchema>;

// Phase 18 — the full Breakdance element catalog, grouped by the editor's
// Add-panel categories. `slug` is the stable identifier stored as a marking's
// breakdance_type and matched by the translator's forced builders; `label` is
// the human name shown in the picker. Tagging any of these is allowed; the
// translator honors the subset it can build today and falls back gracefully
// for the rest (see breakdance-ai.ts).
export type BreakdanceElementDef = {
  slug: string;
  label: string;
  category: string;
};

export const BREAKDANCE_ELEMENTS: BreakdanceElementDef[] = [
  // 1. Basic Elements
  { slug: "Section", label: "Section", category: "Basic" },
  { slug: "Div", label: "Div", category: "Basic" },
  { slug: "Columns", label: "Columns", category: "Basic" },
  { slug: "Grid", label: "Grid", category: "Basic" },
  { slug: "Heading", label: "Heading", category: "Basic" },
  { slug: "Text", label: "Text", category: "Basic" },
  { slug: "RichText", label: "Rich Text", category: "Basic" },
  { slug: "Link", label: "Link", category: "Basic" },
  { slug: "Button", label: "Button", category: "Basic" },
  { slug: "Image", label: "Image", category: "Basic" },
  { slug: "Video", label: "Video", category: "Basic" },
  { slug: "Icon", label: "Icon", category: "Basic" },
  // 2. Blocks
  { slug: "AnimatedHeading", label: "Animated Heading", category: "Blocks" },
  { slug: "IconBox", label: "Icon Box", category: "Blocks" },
  { slug: "ImageBox", label: "Image Box", category: "Blocks" },
  { slug: "CheckboxList", label: "Checkbox List", category: "Blocks" },
  { slug: "FAQ", label: "FAQ", category: "Blocks" },
  { slug: "PricingTable", label: "Pricing Table", category: "Blocks" },
  { slug: "Testimonial", label: "Testimonial", category: "Blocks" },
  { slug: "Marquee", label: "Marquee", category: "Blocks" },
  { slug: "Gallery", label: "Gallery", category: "Blocks" },
  { slug: "ImageComparison", label: "Image Comparison", category: "Blocks" },
  { slug: "Badge", label: "Badge", category: "Blocks" },
  { slug: "LottieAnimation", label: "Lottie Animation", category: "Blocks" },
  { slug: "ProgressBar", label: "Progress Bar & Counter", category: "Blocks" },
  { slug: "Tabs", label: "Tabs", category: "Blocks" },
  // 3. Site Elements
  { slug: "HeaderBuilder", label: "Header Builder", category: "Site" },
  { slug: "MenuBuilder", label: "Menu Builder / WP Menu", category: "Site" },
  { slug: "PopupBuilder", label: "Popup Builder", category: "Site" },
  { slug: "SiteLogo", label: "Logo / Site Logo", category: "Site" },
  { slug: "SearchForm", label: "Search Form", category: "Site" },
  // 4. Advanced Elements
  { slug: "AdvancedSlider", label: "Advanced Slider", category: "Advanced" },
  { slug: "AdvancedAccordion", label: "Advanced Accordion", category: "Advanced" },
  { slug: "GlobalBlock", label: "Global Block", category: "Advanced" },
  { slug: "WrapperLink", label: "Wrapper Link", category: "Advanced" },
  { slug: "CustomCode", label: "Custom Code", category: "Advanced" },
  { slug: "DualButton", label: "Dual Button", category: "Advanced" },
  // 5. Dynamic Data Elements
  { slug: "PostLoopBuilder", label: "Post Loop Builder", category: "Dynamic Data" },
  { slug: "PostsList", label: "Posts List / Post Grid", category: "Dynamic Data" },
  { slug: "TermLoopBuilder", label: "Term Loop Builder", category: "Dynamic Data" },
  { slug: "PostTitle", label: "Post Title / Page Title", category: "Dynamic Data" },
  { slug: "PostMeta", label: "Post Meta", category: "Dynamic Data" },
  { slug: "FeaturedImage", label: "Featured Image", category: "Dynamic Data" },
  { slug: "TemplateContent", label: "Template Content", category: "Dynamic Data" },
  { slug: "Breadcrumbs", label: "Breadcrumbs", category: "Dynamic Data" },
  // 6. Form Elements
  { slug: "FormBuilder", label: "Form Builder", category: "Form" },
  { slug: "LoginForm", label: "Login Form", category: "Form" },
  { slug: "RegisterForm", label: "Register Form", category: "Form" },
  { slug: "ForgotPassword", label: "Forgot Password", category: "Form" },
  // 7. WooCommerce Elements
  { slug: "ProductBuilder", label: "Product Builder", category: "WooCommerce" },
  { slug: "AddToCart", label: "Add To Cart / Quantity", category: "WooCommerce" },
  { slug: "ShopFilters", label: "Shop Filters", category: "WooCommerce" },
  { slug: "ShoppingCart", label: "Shopping Cart / Mini Cart", category: "WooCommerce" },
  { slug: "CheckoutForm", label: "Checkout & Billing Forms", category: "WooCommerce" },
  // 8. Social Elements
  { slug: "FacebookComments", label: "Facebook Comments / Like", category: "Social" },
  { slug: "SocialIcons", label: "Social Icons", category: "Social" },
];

const BREAKDANCE_SLUGS = BREAKDANCE_ELEMENTS.map((e) => e.slug) as [
  string,
  ...string[],
];

// Upsert body for an owner element-marking. `selector` is the stable CSS
// selector the overlay computed; `pageUrl` scopes it to a page.
export const createElementMarkingSchema = z.object({
  pageUrl: z.string().min(1).max(2048),
  selector: z.string().trim().min(1).max(2000),
  breakdanceType: z.enum(BREAKDANCE_SLUGS),
  label: z.string().trim().max(120).optional(),
});

// Body for clearing a marking (DELETE).
export const deleteElementMarkingSchema = z.object({
  pageUrl: z.string().min(1).max(2048),
  selector: z.string().trim().min(1).max(2000),
});

export type ApiElementMarking = {
  id: string;
  projectId: string;
  pageUrl: string;
  selector: string;
  breakdanceType: string;
  label: string | null;
  createdAt: number;
  updatedAt: number;
};

// Messages exchanged between the parent /p page and the overlay running
// inside the proxy iframe. Keep payloads small and serialisable.
//
// `screenX` / `screenY` on new-comment-intent + pin-clicked are
// **iframe-viewport-relative** pixel coordinates of the click / pin
// centre. The parent adds its iframe's getBoundingClientRect().left/top
// to position floating popovers in its own viewport coord space.
export const overlayMessageSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("ready"), pageUrl: z.string() }),
  z.object({
    kind: z.literal("new-comment-intent"),
    position: commentPositionSchema,
    pageUrl: z.string(),
    screenX: z.number().finite(),
    screenY: z.number().finite(),
  }),
  z.object({
    kind: z.literal("pin-clicked"),
    commentId: z.string(),
    screenX: z.number().finite(),
    screenY: z.number().finite(),
  }),
  // Owner drag-to-reposition: emitted once on mouseup if the pin was
  // dragged past the click-vs-drag threshold. Carries the new
  // CommentPosition derived from the drop point.
  z.object({
    kind: z.literal("pin-moved"),
    commentId: z.string(),
    position: commentPositionSchema,
  }),
  z.object({ kind: z.literal("page-changed"), pageUrl: z.string() }),
  z.object({ kind: z.literal("form-blocked") }),
  // Throttled scroll-start signal so the parent can dismiss floating
  // popovers (their anchor pins move with the document, the popover
  // would not, so we close them rather than chase the new screen
  // position frame-by-frame).
  z.object({ kind: z.literal("frame-scrolled") }),
  // Code Mode: owner clicked an element to inspect. Carries a curated
  // set of computed styles + a stable selector so the parent can render
  // a floating inspector popover. `tree` is the full captured subtree
  // (capped) used by the high-fidelity Copy-as-Breakdance path; older
  // overlay bundles may omit it.
  z.object({
    kind: z.literal("element-inspected"),
    screenX: z.number().finite(),
    screenY: z.number().finite(),
    info: z.object({
      tagName: z.string(),
      id: z.string().nullable(),
      classes: z.array(z.string()),
      selector: z.string(),
      text: z.string(),
      rect: z.object({ width: z.number(), height: z.number() }),
      styles: z.record(z.string()),
    }),
    tree: capturedNodeSchema.optional(),
  }),
]);
export type OverlayMessage = z.infer<typeof overlayMessageSchema>;

// Owner-only review modes. Reviewers + client invitees always operate
// in "comment". "view" is a passive explore mode: the overlay paints no
// pins and intercepts no clicks, so the proxied page behaves normally
// for navigating around.
export const reviewModeSchema = z.enum(["comment", "code", "editor", "view"]);
export type ReviewMode = z.infer<typeof reviewModeSchema>;

export const renderPinSchema = z.object({
  id: z.string(),
  position: commentPositionSchema,
  status: z.enum(["open", "resolved"]),
  replyCount: z.number().int().nonnegative(),
  // Authors' initials for the pin label. Server provides a single-char string.
  label: z.string().min(1).max(3),
});
export type RenderPin = z.infer<typeof renderPinSchema>;

// Pending pin = the neutral ghost bubble shown at the click point while the
// reviewer fills in the composer. The parent owns this state and rebroadcasts
// it as part of every render-comments payload, so the overlay never has to
// remember the ghost across paint cycles.
export const pendingPinSchema = z.object({
  position: commentPositionSchema,
});
export type PendingPin = z.infer<typeof pendingPinSchema>;

export const parentMessageSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("render-comments"),
    comments: z.array(renderPinSchema),
    pendingPin: pendingPinSchema.nullable().optional(),
    pageUrl: z.string(),
    // Owner-only capability flag: lets the overlay switch existing pins
    // into draggable mode (grab cursor, mousedown threshold, pin-moved
    // emit). Defaults to false so the share + client surfaces stay
    // strictly read-only.
    canMovePins: z.boolean().optional(),
    // Current owner-tool. Defaults to "comment". In "code", the
    // overlay paints a hover highlight + routes clicks to
    // element-inspected. In "editor" (future) the overlay would
    // enable contenteditable on text nodes — currently no-op.
    mode: reviewModeSchema.optional(),
  }),
  z.object({ kind: z.literal("scroll-to-comment"), commentId: z.string() }),
  z.object({ kind: z.literal("cancel-new-comment") }),
]);
export type ParentMessage = z.infer<typeof parentMessageSchema>;

// Owner-only API body for repositioning a comment without touching its
// other fields. The route enforces auth and project ownership.
export const updateCommentPositionSchema = z.object({
  position: commentPositionSchema,
});

// Phase 10 — Styleguide + Element Guide.
//
// Both surfaces are on-demand extractions of the project's target site.
// Server fetches every page in `project_pages` (up to discoverPages' cap),
// parses inline + same-origin stylesheets for the styleguide and runs a
// single HTMLRewriter pass per page for the element guide. The aggregated
// JSON lives in `project_styleguides.tokens_json` / `project_element_guides.categories_json`
// with one row per project — re-running UPSERTs.

export type StyleColor = { value: string; count: number };     // canonical "#rrggbb" or "#rrggbbaa"
export type StyleFont = { family: string; count: number };     // first family in the stack
export type StyleSize = { value: string; count: number };      // "16px", "1.25rem"
export type StyleWeight = { value: number; count: number };

export type StyleHeadingSample = {
  level: 1 | 2 | 3;
  text: string;
};

export type ApiStyleguideTokens = {
  colors: StyleColor[];
  fonts: StyleFont[];
  sizes: StyleSize[];
  weights: StyleWeight[];
  faviconUrl: string | null;
  logoUrl: string | null;
  headings: StyleHeadingSample[];
};

export type ApiStyleguide = {
  tokens: ApiStyleguideTokens;
  sourcePagesCount: number;
  runAt: number; // epoch seconds
};

export type ElementCategoryId =
  | "nav"
  | "hero"
  | "headings"
  | "buttons"
  | "forms"
  | "cards"
  | "footer"
  | "media"
  | "lists"
  | "links";

export type ElementVariant = {
  selector: string;        // "button.btn-primary"
  snippet: string;         // truncated outerHTML, ≤500 chars
  firstPagePath: string;   // first page this variant was seen on
  count: number;           // total occurrences across all pages
};

export type ElementCategory = {
  id: ElementCategoryId;
  label: string;
  variants: ElementVariant[];
};

export type ApiElementGuide = {
  categories: ElementCategory[];
  sourcePagesCount: number;
  runAt: number;
  // Absolute, same-origin <link rel="stylesheet"> URLs collected from every
  // page we crawled. The dashboard injects these into the preview iframe so
  // variant snippets render with the target site's real styling. Deduped
  // server-side and capped to keep the payload reasonable.
  cssHrefs: string[];
  // Base URL the preview iframe should use for resolving any relative refs
  // left in the snippet (e.g. <img src="/logo.svg">). First crawled page.
  baseUrl: string | null;
};

// Comment shape returned by the API. Always include the parsed position so
// the dashboard doesn't have to JSON.parse the stored blob.
export type ApiComment = {
  id: string;
  parentId: string | null;
  pageUrl: string;
  position: CommentPosition;
  authorName: string;
  authorEmail: string;
  body: string;
  status: "open" | "resolved";
  createdAt: number;
  resolvedAt: number | null;
};
